import { reflect } from '@effector/reflect';
import { TextInput, Checkbox, Stack, Text } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { PenSquare } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ACCOUNT_NAME_MIN_LENGTH, ACCOUNT_NOTE_MAX_LENGTH } from 'entities/account';
import { viewerModel } from 'entities/viewer';
import { AccountWithWalletsAndPermissions } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { createModalForm, ModalFormActionIconProps, ModalFormProps } from 'shared/ui';
import { EditAccountModel } from '../../model';

export const createEditAccountModal = (model: EditAccountModel) => {
	const ModalForm = createModalForm(model);

	const NameField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'edit-account-name-input',
			label: translated('edit-account.name'),
			placeholder: translated('edit-account.name'),
			errorProps: { 'data-test-id': 'edit-account-name-input-error' },
			error: combine(
				[model.form.fields.name.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-account.name'),
						minLength: ACCOUNT_NAME_MIN_LENGTH,
					}),
			),
			value: model.form.fields.name.$value,
			onChange: (e) => model.form.fields.name.change(e.target.value),
			required: true,
		},
	});

	const NoteField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'edit-account-note-input',
			label: translated('edit-account.note'),
			placeholder: translated('edit-account.note'),
			errorProps: { 'data-test-id': 'edit-account-note-input-error' },
			error: combine(
				[model.form.fields.note.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-account.note'),
						maxLength: ACCOUNT_NOTE_MAX_LENGTH,
					}),
			),
			value: model.form.fields.note.$value,
			onChange: (e) => model.form.fields.note.change(e.target.value),
		},
	});

	const CreateSubAccountsField = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'edit-account-create-subaccounts-checkbox',
			label: translated('edit-account.write-subaccounts'),
			disabled: model.form.fields.canCreateSubAccounts.$disabled,
			checked: model.form.fields.canCreateSubAccounts.$value,
			onChange: (e) => model.form.fields.canCreateSubAccounts.change(e.target.checked),
		},
	});

	const WriteCashdesksFIeld = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'edit-account-write-cashdesks-checkbox',
			label: translated('edit-account.write-cashdesks'),
			disabled: model.form.fields.canWriteCashdesks.$disabled,
			checked: model.form.fields.canWriteCashdesks.$value,
			onChange: (e) => model.form.fields.canWriteCashdesks.change(e.target.checked),
		},
	});

	const WriteUsersField = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'edit-account-write-users-checkbox',
			label: translated('edit-account.write-users'),
			disabled: model.form.fields.canWriteUsers.$disabled,
			checked: model.form.fields.canWriteUsers.$value,
			onChange: (e) => model.form.fields.canWriteUsers.change(e.target.checked),
		},
	});

	const EditLimitsField = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'edit-account-edit-limits-checkbox',
			label: translated('edit-account.edit-limits'),
			disabled: model.form.fields.canEditLimits.$disabled,
			checked: model.form.fields.canEditLimits.$value,
			onChange: (e) => model.form.fields.canEditLimits.change(e.target.checked),
		},
	});

	const BlockGamesField = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'edit-account-block-games-checkbox',
			label: translated('edit-account.block-games'),
			disabled: model.form.fields.canBlockGames.$disabled,
			checked: model.form.fields.canBlockGames.$value,
			onChange: (e) => model.form.fields.canBlockGames.change(e.target.checked),
		},
	});

	const EditGameSettingsField = reflect({
		view: Checkbox,
		bind: {
			'data-test-id': 'edit-account-edit-game-settings-checkbox',
			label: translated('edit-account.edit-game-settings'),
			disabled: model.form.fields.canEditGameSettings.$disabled,
			checked: model.form.fields.canEditGameSettings.$value,
			onChange: (e) => model.form.fields.canEditGameSettings.change(e.target.checked),
		},
	});

	const EditAccountModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [pending, pristine, closed] = useUnit([
			model.editAccountMutation.$pending,
			model.form.$pristine,
			model.modalClosed,
		]);

		return (
			<ModalForm
				ref={ref}
				title={t('edit-account.title')}
				cancelButtonProps={{
					children: t('edit-account.cancel'),
				}}
				submitButtonProps={{
					children: t('edit-account.save'),
					loading: pending,
					disabled: pristine,
				}}
				onExitTransitionEnd={closed}
				{...props}
			>
				<NameField />
				<NoteField />
				<Stack gap={12}>
					<Text
						size='sm'
						fw={500}
					>
						{t('edit-account.permissions')}
					</Text>
					<CreateSubAccountsField />
					<WriteCashdesksFIeld />
					<WriteUsersField />
					<EditLimitsField />
					<EditGameSettingsField />
					<BlockGamesField />
				</Stack>
			</ModalForm>
		);
	});

	const EditAccountModalActionIcon = forwardRef<
		HTMLButtonElement,
		ModalFormActionIconProps<AccountWithWalletsAndPermissions>
	>(({ modalData, ...props }, ref) => {
		const [viewerAccountId, canCreateSubAccounts] = useUnit([
			viewerModel.$accountId,
			viewerModel.$canCreateSubAccounts,
		]);

		return (
			<ModalForm.ActionIcon
				ref={ref}
				data-test-id='edit-account-action-icon'
				variant='ghost-secondary'
				disabled={viewerAccountId === modalData.id || !canCreateSubAccounts}
				modalData={modalData}
				{...props}
			>
				<PenSquare />
			</ModalForm.ActionIcon>
		);
	});

	return Object.assign(EditAccountModal, {
		ActionIcon: EditAccountModalActionIcon,
	});
};
